// Outbound video encoder caps for the monitor.
//
// Left to browser defaults, each viewer's encoder ramps from a conservative
// bitrate (mushy first seconds, exactly when a parent glances at the baby) and
// prefers framerate over resolution — backwards for a near-static nursery where
// the parent wants to read position and breathing, not motion. Because PeerJS
// builds one RTCPeerConnection per viewer, an uncapped monitor also runs one
// full-rate encoder per parent for hours, which heats the phone and can itself
// trip the camera-freeze recovery path.
//
// Caps chosen for an overnight monitor: a firm bitrate ceiling for a stable
// picture, a low framerate (a sleeping baby needs no more), and resolution held
// over framerate when bandwidth dips.
export const MAX_BITRATE = 700_000; // ~3 GB over a 10-hour night
export const MAX_FRAMERATE = 15;

// Apply the caps to a peer connection's video sender. Idempotent and
// best-effort: getParameters()/setParameters() can reject transiently (e.g.
// before the first negotiation on Firefox), and a miss just means defaults for
// that connection until the next call. Safe to run again after replaceTrack.
export async function applyVideoSendCaps(pc) {
  if (!pc || typeof pc.getSenders !== 'function') return;
  const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
  if (!sender || typeof sender.getParameters !== 'function') return;
  try {
    const params = sender.getParameters();
    // Firefox leaves encodings empty until negotiation completes; seed one so the
    // caps land instead of silently no-op'ing.
    if (!params.encodings || !params.encodings.length) params.encodings = [{}];
    params.encodings[0].maxBitrate = MAX_BITRATE;
    params.encodings[0].maxFramerate = MAX_FRAMERATE;
    // A still scene should lose frames, not sharpness, before it drops resolution.
    params.degradationPreference = 'maintain-resolution';
    await sender.setParameters(params);
  } catch (e) {
    /* transient — caps reapply on the next connect / track replace */
  }
}
